import type { MembershipSaveRequest, MembershipDetail } from '../../../utils/membership';
import { apiDateTimeToDatetimeLocalValue } from '../../../utils/dateTime';

/** 멤버십 정보 탭 폼 데이터 */
export interface MembershipFormData {
  status: string;
  type: string;
  title: string;
  startAt: string;
  endAt: string;
  timezone: string;
  paidMembership: number;
  membershipLevel: number;
  rewardPolicyOwner: string;
  rewardType: string;
  rewardTitle: string;
  settlementOwner: string;
  validType: number;
  validPeriod: number;
  validAt: string;
  stampSetCount: number;
  voucherExchangeMethod: string;
  voucherUseDelay: number;
  pointRewardTarget: string;
  pointExpireCycle: string;
  pointRatio: number;
  pointRoundingMode: string;
  pointRoundingScale: number;
  note: string;
}

/** 멤버십 정보 탭 폼 에러 */
export interface MembershipFormErrors {
  status?: string;
  type?: string;
  title?: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  paidMembership?: string;
  membershipLevel?: string;
  rewardPolicyOwner?: string;
  rewardType?: string;
  rewardTitle?: string;
  settlementOwner?: string;
  validType?: string;
  validPeriod?: string;
  validAt?: string;
  stampSetCount?: string;
  voucherExchangeMethod?: string;
  voucherUseDelay?: string;
  pointRewardTarget?: string;
  pointExpireCycle?: string;
  pointRatio?: string;
  pointRoundingMode?: string;
  pointRoundingScale?: string;
  note?: string;
}

/** 멤버십 정보 탭 기본값 (생성 모드) */
export const DEFAULT_MEMBERSHIP_FORM_DATA: MembershipFormData = {
  status: 'PENDING',
  type: 'REWARD',
  title: '',
  startAt: '',
  endAt: '',
  timezone: 'Asia/Seoul',
  paidMembership: 0,
  membershipLevel: 0,
  rewardPolicyOwner: 'HEADQUARTERS',
  rewardType: 'NONE',
  rewardTitle: '',
  settlementOwner: 'SHOP',
  validType: 0,
  validPeriod: 0,
  validAt: '',
  stampSetCount: 0,
  voucherExchangeMethod: 'STEP',
  voucherUseDelay: 0,
  pointRewardTarget: 'PAYMENT',
  pointExpireCycle: 'YEAR',
  pointRatio: 0,
  pointRoundingMode: 'HALF_UP',
  pointRoundingScale: 1,
  note: '',
};

/** 스탬프 리워드일 때만 교환권 설정 탭 필요 */
export function membershipNeedsVoucherTab(rewardType: string): boolean {
  return rewardType === 'STAMP';
}

/** 생성/수정 API 요청 body 구성 */
export function buildMembershipSaveRequest(
  data: MembershipFormData,
  toIsoUtc: (value: string) => string
): MembershipSaveRequest {
  const request: MembershipSaveRequest = {
    title: data.title,
    status: data.status,
    type: data.type,
    note: data.note || '',
    rewardType: data.rewardType,
    rewardPolicyOwner: data.rewardPolicyOwner,
    paidMembership: data.paidMembership,
    membershipLevel: data.membershipLevel,
    timezone: data.timezone?.trim() || 'Asia/Seoul',
  };

  if (data.startAt?.trim()) request.startAt = toIsoUtc(data.startAt);
  if (data.endAt?.trim()) request.endAt = toIsoUtc(data.endAt);

  if (data.rewardType === 'STAMP' || data.rewardType === 'POINT') {
    request.rewardTitle = data.rewardTitle;
    request.settlementOwner = data.settlementOwner;
    request.validType = data.validType;
    if (Number(data.validType) === 0) request.validPeriod = data.validPeriod;
    else if (Number(data.validType) === 1 && data.validAt?.trim()) {
      request.validAt = toIsoUtc(data.validAt);
    }
  }

  if (data.rewardType === 'STAMP') {
    request.stampSetCount = data.stampSetCount;
    request.voucherExchangeMethod = data.voucherExchangeMethod;
    request.voucherUseDelay = data.voucherUseDelay;
  }

  if (data.rewardType === 'POINT') {
    request.pointRewardTarget = data.pointRewardTarget;
    request.pointExpireCycle = data.pointExpireCycle;
    request.pointRatio = data.pointRatio;
    request.pointRoundingMode = data.pointRoundingMode;
    request.pointRoundingScale = data.pointRoundingScale;
  }

  return request;
}

export const buildMembershipCreateRequest = buildMembershipSaveRequest;
export const buildMembershipUpdateRequest = buildMembershipSaveRequest;

/** 서버 필드 에러 매핑 */
export const MEMBERSHIP_FIELD_MAPPING: Record<string, keyof MembershipFormErrors> = {
  startAt: 'startAt',
  endAt: 'endAt',
  timezone: 'timezone',
  title: 'title',
  status: 'status',
  type: 'type',
  rewardType: 'rewardType',
  rewardPolicyOwner: 'rewardPolicyOwner',
  paidMembership: 'paidMembership',
  membershipLevel: 'membershipLevel',
  rewardTitle: 'rewardTitle',
  settlementOwner: 'settlementOwner',
  validType: 'validType',
  validPeriod: 'validPeriod',
  validAt: 'validAt',
  stampSetCount: 'stampSetCount',
  voucherExchangeMethod: 'voucherExchangeMethod',
  voucherUseDelay: 'voucherUseDelay',
  pointRewardTarget: 'pointRewardTarget',
  pointExpireCycle: 'pointExpireCycle',
  pointRatio: 'pointRatio',
  pointRoundingMode: 'pointRoundingMode',
  pointRoundingScale: 'pointRoundingScale',
  note: 'note',
};

export const MEMBERSHIP_CREATE_FIELD_MAPPING = MEMBERSHIP_FIELD_MAPPING;

/** 상세 API 응답 → 폼 데이터 */
export function mapMembershipDetailToFormData(membership: MembershipDetail): MembershipFormData {
  return {
    status: membership.status || 'ACTIVE',
    type: membership.type || 'REWARD',
    title: membership.title || '',
    startAt: membership.startAt ? apiDateTimeToDatetimeLocalValue(membership.startAt) : '',
    endAt: membership.endAt ? apiDateTimeToDatetimeLocalValue(membership.endAt) : '',
    timezone: membership.timezone?.trim() || 'Asia/Seoul',
    paidMembership: membership.paidMembership || 0,
    membershipLevel: membership.membershipLevel || 0,
    rewardPolicyOwner: membership.rewardPolicyOwner || 'HEADQUARTERS',
    rewardType: membership.rewardType || 'NONE',
    rewardTitle: membership.rewardTitle || '',
    settlementOwner: membership.settlementOwner || 'SHOP',
    validType: membership.validType ?? 0,
    validPeriod: membership.validPeriod ?? 0,
    validAt: membership.validAt ? apiDateTimeToDatetimeLocalValue(membership.validAt) : '',
    stampSetCount: membership.stampSetCount || 0,
    voucherExchangeMethod: membership.voucherExchangeMethod || 'STEP',
    voucherUseDelay: membership.voucherUseDelay || 0,
    pointRewardTarget: membership.pointRewardTarget || 'PAYMENT',
    pointExpireCycle: membership.pointExpireCycle || 'YEAR',
    pointRatio: membership.pointRatio || 0,
    pointRoundingMode: membership.pointRoundingMode || 'HALF_UP',
    pointRoundingScale: membership.pointRoundingScale ?? 1,
    note: membership.note || '',
  };
}
